import React, { useState, useEffect, useContext } from 'react';
import { UserContext, CompanyContext } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Building2, 
  ChevronDown, 
  Check, 
  Plus,
  Briefcase
} from 'lucide-react';

const CompanySwitcher = () => {
  const { user, API } = useContext(UserContext);
  const { company, updateCompany } = useContext(CompanyContext);
  const [userCompanies, setUserCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSwitcher, setShowSwitcher] = useState(false);

  useEffect(() => {
    if (user && user.multiple_companies_access) {
      fetchUserCompanies();
    }
  }, [user]);

  const fetchUserCompanies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/company/user/${user.id}/all`);
      setUserCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching user companies:', error);
      setUserCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const switchToCompany = (selectedCompany) => {
    updateCompany(selectedCompany);
    setShowSwitcher(false);
    toast.success(`Basculé vers: ${selectedCompany.raison_sociale}`);
  };

  // Ne pas afficher le sélecteur si pas d'accès multi-entreprises ou moins de 2 entreprises
  if (!user || !user.multiple_companies_access || userCompanies.length < 2) {
    return null;
  }

  return (
    <Dialog open={showSwitcher} onOpenChange={setShowSwitcher}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center gap-2 bg-white/70 border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 transition-all duration-200"
          data-testid="company-switcher-btn"
        >
          <Briefcase className="h-4 w-4 text-indigo-600" />
          <span className="hidden sm:inline text-sm font-medium text-indigo-700">
            Multiple Entreprises
          </span>
          <Badge variant="secondary" className="bg-indigo-100 text-indigo-800 ml-1">
            {userCompanies.length}
          </Badge>
          <ChevronDown className="h-3 w-3 text-indigo-600" />
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-indigo-600" />
            Sélectionner une Entreprise
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-6">
              <div className="spinner mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des entreprises...</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Choisissez l'entreprise que vous souhaitez gérer :
              </p>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {userCompanies.map((comp) => (
                  <button
                    key={comp.id}
                    onClick={() => switchToCompany(comp)}
                    className={`w-full p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                      company && company.id === comp.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-25'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center overflow-hidden">
                          {comp.logo ? (
                            <img 
                              src={comp.logo} 
                              alt="Logo" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Building2 className="h-5 w-5 text-white" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {comp.raison_sociale}
                          </p>
                          <p className="text-xs text-gray-500">
                            NCC: {comp.numero_ncc}
                          </p>
                          <p className="text-xs text-gray-500">
                            Propriétaire: {comp.prenom_proprietaire} {comp.nom_proprietaire}
                          </p>
                        </div>
                      </div>
                      
                      {company && company.id === comp.id && (
                        <div className="flex items-center">
                          <Check className="h-5 w-5 text-indigo-600" />
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              
              <div className="border-t border-gray-200 pt-4 mt-4">
                <Button
                  onClick={() => {
                    setShowSwitcher(false);
                    // Rediriger vers création d'entreprise
                    window.location.href = '/company/create';
                  }}
                  variant="outline"
                  className="w-full border-2 border-dashed border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                  data-testid="create-new-company-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Créer une Nouvelle Entreprise
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompanySwitcher;